// Shared domain types, re-exported from the shared contract (the single source of
// truth for the API, CLI, and Web UI). Only web-view-specific shapes are defined
// locally below.

import type { DevhubDeletedPageLinkEntry, DevhubRole } from "../../../packages/shared/src/types.js";
export type { DevhubDiscardedDraftRow } from "../../../packages/shared/src/types.js";
export type { GuideCheck, GuideCollection, GuideCreateInput, GuideCreateResult, GuideList, GuidePage, GuidePageContent, GuideSaveResult } from "../../../packages/shared/src/types.js";
export { AgentRuntimeState, AGENT_EFFORTS } from "../../../packages/shared/src/types.js";
export type {
  DevhubBatchLaunch,
  PolarisLaunchBatchRef,
  PolarisLaunchDetail,
  PolarisLaunchListing,
  PolarisLaunchResolved,
  PolarisLaunchView,
} from "../../../packages/shared/src/devhub-polaris.js";
export type {
  ClaudeSettings,
  McpAskResult,
  McpConnection,
  McpOverview,
  McpToolCallTrace,
  McpToolSummary,
} from "../../../packages/shared/src/mcp.js";
// The notification tab vocabulary is shared, not mirrored: the bell renders the
// tabs, the store filters by them and the route validates against them, and
// three copies of three words is how a tab comes to offer an action its own
// server refuses (ENG-56868).
export {
  NOTIFICATION_BUCKETS,
  ACTION_LABELS as NOTIFICATION_ACTION_LABELS,
  actionsFor as notificationActionsFor,
  bucketOf as notificationBucketOf,
  needsConfirm as notificationNeedsConfirm,
} from "../../../packages/shared/src/notification-buckets.js";
export type { NotificationAction, NotificationBucket } from "../../../packages/shared/src/notification-buckets.js";

export type {
  Agent,
  AgentActivity,
  CodeSample,
  DevhubAsset,
  DevhubBookmarkProblem,
  DevhubBookmarkReport,
  DevhubBrokenBookmark,
  DevhubAssetReplaceOutcome,
  DevhubAssetReplacePage,
  DevhubAssetReplacePlan,
  DevhubAssetReplaceResult,
  DevhubAssetRescanResult,
  DevhubAssetDeleteResult,
  DevhubAssetUnlinkOutcome,
  DevhubAssetUnlinkPage,
  DevhubAssetUnlinkPlan,
  DevhubAssetList,
  DevhubAssetSource,
  DevhubAssetSummary,
  DevhubAssetFolder,
  DevhubAssetFolderCounts,
  DevhubAssetFolderKey,
  DevhubAssist,
  DevhubAssistMode,
  DevhubAssistStatus,
  DevhubAuditEntry,
  DevhubComponent,
  DevhubComponentRegistry,
  DevhubComponentStatus,
  DevhubComponentSummary,
  DevhubComponentReference,
  DevhubComponentUsage,
  DevhubConfigStatus,
  DevhubDirectoryEntry,
  DevhubDraft,
  DevhubDraftEvent,
  DevhubReleaseFeedChoice,
  DevhubPropertyDefinition,
  DevhubPropertyValue,
  DevhubPagePropertiesFile,
  DevhubPropertyRetiringRow,
  DevhubSdkReleaseFeedAnswer,
  DevhubSdkReleaseFeedInfo,
  DevhubDraftStatus,
  DevhubAnalyticsCountry,
  DevhubAnalyticsDay,
  DevhubAnalyticsFlow,
  DevhubAnalyticsFrictionCount,
  DevhubAnalyticsFrictionDetail,
  DevhubAnalyticsFrictionSubject,
  DevhubAnalyticsPage,
  DevhubAnalyticsPageCount,
  DevhubAnalyticsShapeCount,
  DevhubFrictionSignal,
  DevhubVisitShape,
  DevhubAnalyticsQuestion,
  DevhubAnalyticsReport,
  DevhubAiArtifact,
  DevhubAiContextReport,
  DevhubAiCoverage,
  DevhubAiGuidance,
  DevhubAiGuidanceState,
  DevhubAiHeldBackPage,
  DevhubAiRuntime,
  DevhubAiSettingsRow,
  DevhubAiSettingsState,
  DevhubAiSectionCoverage,
  DevhubAiUsageDay,
  DevhubAiUsageReport,
  DevhubApiSpecFreshness,
  DevhubApiSyncChange,
  DevhubApiSyncItem,
  DevhubApiSyncPending,
  DevhubApiSyncReport,
  DevhubApiSyncResult,
  DevhubApiSyncRun,
  DevhubApiSyncRunChange,
  DevhubApiSyncRunSummary,
  DevhubBrubeckDocument,
  DevhubBrubeckEdit,
  DevhubBrubeckReviewPr,
  DevhubBrubeckReviewState,
  DevhubBrubeckSource,
  DevhubBrubeckRelease,
  DevhubBrubeckReleaseDetail,
  DevhubBrubeckReleases,
  DevhubAnalyticsSessions,
  DevhubAnalyticsTerm,
  DevhubAlarmState,
  DevhubLambdaHealth,
  DevhubFeedback,
  DevhubFeedbackAction,
  DevhubFeedbackRating,
  DevhubFeedbackReply,
  DevhubFeedbackReplyRole,
  DevhubFeedbackSource,
  DevhubBrokenLink,
  DevhubLinkProblem,
  DevhubHotlinkItem,
  DevhubHotlinkReport,
  DevhubLinkReport,
  DevhubLiveFeedbackFeed,
  DevhubLiveFeedbackItem,
  DevhubLiveFeedbackTally,
  DevhubNewPageTarget,
  DevhubCorpusInfo,
  DevhubTargetName,
  DevhubPageWorkflow,
  DevhubPageBar,
  DevhubPageDeletion,
  DevhubNesting,
  DevhubOrder,
  DevhubPageContent,
  DevhubPageEntry,
  DevhubRedirect,
  DevhubRedirectSettings,
  DevhubRedirectTarget,
  DevhubRole,
  DevhubSlugCollision,
  DevhubVisibility,
  DevhubVisibilityDrift,
  DevhubVisibilityDriftEntry,
  DevhubVisibilityReconcileResult,
  DevhubSearchEntry,
  DevhubSectionOwner,
  DevhubFeedbackSpamDecision,
  DevhubFeedbackSpamReason,
  FeedbackBucket,
  DevhubSdkBranch,
  DevhubSdkRelease,
  DevhubSdkReleaseRef,
  DevhubSdkReleaseFolder,
  DevhubSdkSummary,
  DevhubSdkTokenReport,
  DevhubSdkContentFile,
  DevhubSdkPage,
  DevhubSdkTree,
  DevhubSdkContentPr,
  DevhubSdkPublishBlocker,
  DevhubSdkPageState,
  DevhubSdkPageDiff,
  DevhubSdkDeletedPage,
  DevhubSdkDiff,
  DevhubSdkChangedSection,
  DevhubSdkPublishPlan,
  DevhubSdkEdit,
  DevhubSdkStagedChanges,
  DevhubSdkReviewPr,
  DevhubSdkCommit,
  DevhubSdkChangedDoc,
  DevhubSdkBranchUpdate,
  DevhubStatusAlarm,
  DevhubStatusErrorGroup,
  DevhubStatusCloudFront,
  DevhubStatusComponent,
  DevhubStatusErrorLines,
  DevhubStatusIncident,
  DevhubStatusLevel,
  DevhubStatusLogLine,
  DevhubStatusPeriod,
  DevhubStatusPoint,
  DevhubStatusReport,
  DevhubAnalyticsAudience,
  DevhubAnalyticsAudienceDay,
  DevhubAnalyticsMcp,
  DevhubAnalyticsMcpCount,
  DevhubAnalyticsTrafficPath,
  DevhubAudienceClass,
  DevhubPathKind,
  DevhubTag,
  DevhubTeam,
  DevhubTeamSummary,
  DevhubBatch,
  DevhubBatchPage,
  DevhubBatchPublishOutcome,
  DevhubBatchStatus,
  DevhubBatchChange,
  DevhubBatchChangeOutcome,
  DevhubCrossPagePrompt,
  DevhubCrossPageStaging,
  DevhubMenuStaging,
  DevhubHeldPage,
  DevhubHeldPosition,
  DevhubStatusUptime,
  DevhubStatusWaf,
  DevhubStatusWafRule,
  DevhubUserRole,
  DevhubVariable,
  DevhubVariableReference,
  DevhubDeletedPage,
  DevhubDeletedPageLinkEntry,
  DevhubVariableCategory,
  DevhubVariableSummary,
  DevhubVariableUsage,
  HubNotification,
  HumanPresence,
  KnowledgeBaseStatus,
  NotificationKind,
  OmniUserEntry,
  Output,
  AgentRuntime,
  AgentEffort,
  AgentRuntimeEntry,
  AgentRuntimeSpend,
  RepoClone,
  RepoReadiness,
  RepoReadinessScanStatus,
  Session,
  SessionUser,
  SummaryProposal,
  Task,
  TaskPlan,
  TaskProgress,
} from "../../../packages/shared/src/types.js";

// Enums are values, not just types — re-export them as such.
export {
  AgentActivityState,
  AgentStatus,
  ApprovalStatus,
  OutputKind,
  RepoCloneStatus,
  TaskProgressKind,
  TaskStatus,
} from "../../../packages/shared/src/types.js";

// Runtime helpers from the shared contract (values, not types).
export { isPubliclyVisible } from "../../../packages/shared/src/types.js";

// Per-role capability blurbs shown in the DevHub CMS settings UI (value, not a
// type). Source of truth is the API's devhub-permissions.ts; see the constant.
export { ROLE_DESCRIPTIONS } from "../../../packages/shared/src/types.js";

// The edge-traffic vocabulary (values, not types) — the machine-readable kinds
// the Analytics tab's file panel lists, and the URL-path → corpus-path rule its
// page table links with. Both live in devhub-traffic.ts beside the reasoning
// the API shares; re-exported here so no component reaches across the workspace
// with a relative path of its own.
export { DEVHUB_FILE_KINDS, pagePathForUrlPath } from "../../../packages/shared/src/devhub-traffic.js";

// The canonical org enum lives in the shared constants; the web has always
// called it OrgId, so keep that name as an alias for the many call sites.
export { Org as OrgId } from "../../../packages/shared/src/constants.js";

export enum View {
  Home = "home",
  Hub = "hub",
  Repos = "repos",
  Docs = "docs",
  // The Atlas — the graph-linked knowledge base whose pages are the graph nodes.
  Graph = "graph",
  // The whole DevHub surface (Docs / API Reference / sections) is one view; the
  // active tab + open page live in the URL path/query, not in distinct views.
  Devhub = "devhub",
  // Standalone, brand-styled public docs preview (opened in a new tab).
  Preview = "preview",
  Agents = "agents",
  // External MCP connections for the selected org (admin-only) — the internal
  // Claude process' tool sources, each with an overview of what it provides.
  Mcp = "mcp",
  Skills = "skills",
  Sessions = "sessions",
  Tasks = "tasks",
  // Autonomous agent runtimes — one container per repo. Admin-only, global (not
  // org-scoped): one place to see every runtime across the caller's orgs.
  Runtime = "runtime",
  // Omni Hub admin settings — a global (non-org) view, only shown to admins.
  Settings = "settings",
  // Internal guide collections for the selected org (content/<org>/internal/),
  // listed under DevHub in the rail. Shown only for orgs that have any.
  Guides = "guides",
}

// One of the six summarized .agents/ categories.
export enum AgentCategory {
  Guides = "guides",
  Loop = "loop",
  Memory = "memory",
  Scripts = "scripts",
  Skills = "skills",
  Standards = "standards",
}

// How a common file should be rendered.
export enum CommonFileKind {
  Markdown = "markdown",
  Shell = "shell",
  Text = "text",
}

// A file in a category's common dir (Agents Common, file-based categories).
export interface CommonFile {
  path: string; // relative to <category>/common, e.g. "loop.md"
  title: string;
  kind: CommonFileKind;
}

// A repo's agent knowledge as grouped under system/org/<org>/agents/ for one org.
export interface AgentRepoEntry {
  repo: string;
  description?: string | null; // the agent's lede (first paragraph of its spec)
  categories: AgentCategory[];
  spec: string | null; // .agents/<repo>-agent.md filename, or null if absent
  commit: string | null; // pinned commit the summaries were generated against
  agentsUrl: string | null; // commit-pinned host URL for the repo's .agents/ dir
  specUrl: string | null; // commit-pinned host URL for the repo's agent spec file
}

// A file in a repo's live .agents/ tree (served via the mirror symlink).
export interface AgentFileEntry {
  path: string; // relative to .agents/, e.g. "guides/plans.md"
  group: string; // top-level segment (category), or "" for files directly in .agents/
}

// The full .agents/ tree for a repo, read live via the mirror symlink (no duplication).
export interface AgentTree {
  org: string;
  repo: string;
  spec: string | null; // the *-agent.md spec filename, or null
  files: AgentFileEntry[];
}

// The signed-in user, resolved server-side (GET /api/me): the Okta-verified
// identity when deployed, or the shared local default off-Okta.
export interface Me {
  sub: string;
  email: string;
  name: string | null;
  // DevHub CMS role + team (resolved server-side). Off-Okta dev → "admin".
  // `role`/`team` are the PRIMARY role/team; `roles`/`teams` below hold every one.
  role?: DevhubRole;
  team?: string | null;
  // Whether this person leads a DevHub team. A lead manages their own team's
  // roster and nothing else (ENG-54991), and the Teams tab's gate is synchronous —
  // so the fact rides on the session rather than costing a `/teams/mine` fetch.
  leadsTeam?: boolean;
  // App-level Omni Hub admin (Settings + repository management). Distinct from the
  // DevHub `role` above — resolved server-side from the token's Okta groups.
  admin?: boolean;
  // The resolved RBAC principal (GET /api/me, additive). Present whether or not
  // RBAC_ENABLED — off, the server derives it from today's logic; on, from the
  // labs-rbac PDP. The Web UI reads these to show the caller's access and to gate
  // affordances off `permissions` (the server still enforces). Admin ⇒ `orgs` may
  // be empty (all-orgs via the admin flag).
  orgs?: string[]; // orgs this user may act within
  teams?: string[]; // teams the user belongs to (SME scope / departments)
  roles?: string[]; // every role held (empty ⇒ the app's default role)
  permissions?: string[]; // flat capability list a client may pre-check
}

// Omni Hub admin status (GET /api/admin/status) — the admin Okta group plus the
// environment-driven config the app depends on. Admin-only. Secrets are never
// returned (the GitHub token is reported only as present / absent).
export interface AdminStatus {
  // The Okta group whose members are Omni Hub admins (OMNI_HUB_ADMIN_GROUP).
  adminGroup: string;
  oktaConfigured: boolean;
  /** Per-org repo-token presence (org → configured?), keyed by Org. */
  orgTokens: Record<string, boolean>;
  repositoriesDir: string | null;
  /** Whether the background repo syncer runs on its interval (REPO_SYNC_ENABLED).
   *  When false, automatic sync is paused and only manual "Sync now" runs. */
  repoSyncEnabled: boolean;
  repoSyncIntervalMs: number;
  image: string | null;
  registry: string | null;
}

// Health heartbeat for one background worker (GET /api/admin/health). Mirrors the
// server's WorkerHealth (core/job-health.ts). Admin-only, per-container.
export interface WorkerHealth {
  name: string;
  intervalMs: number | null;
  running: boolean;
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastSuccessAt: string | null;
  lastOutcome: "ok" | "error" | null;
  lastError: string | null;
  consecutiveFailures: number;
  totalRuns: number;
  lastMeta: Record<string, unknown> | null;
}

// System health snapshot (GET /api/admin/health): background-worker heartbeats
// plus this container's process vitals.
export interface SystemHealth {
  now: string;
  uptimeSec: number;
  rssBytes: number;
  repoSyncStallMs: number;
  /** Whether the background repo syncer runs on its interval. When false, the
   *  repo-syncer worker is deliberately paused (manual "Sync now" only). */
  repoSyncEnabled: boolean;
  /** Event-loop lag at the last sample, and the worst spike in the last ten
   *  seconds. A blocked loop stalls every endpoint at once, so this is what
   *  separates "this request is slow" from "the whole container is stalled". */
  loopLagMs: number;
  loopLagPeakMs: number;
  workers: WorkerHealth[];
}

// One captured internal log line (GET /api/admin/logs). Mirrors the server's
// InternalLogEntry ring buffer (shared/src/log.ts).
export interface InternalLogEntry {
  time: string;
  level: "debug" | "info" | "warn" | "error";
  msg: string;
  fields: Record<string, unknown>;
}

// One recent, human-readable change (GET /api/changelog) — a squash-merged PR
// on develop, stripped of its `type(scope):` prefix so it reads as a sentence.
export interface ChangelogEntry {
  number: number;
  sentence: string;
  url: string;
  mergedAt: string;
}

// Version badge popover content (GET /api/changelog). `version` is the most
// recently merged PR number (trunk is squash-merge-only, so PR# already
// increments roughly 1:1 with what ships) — not a semver, just a readable build marker.
export interface Changelog {
  version: string;
  lastCommit: ChangelogEntry | null;
  deployUrl: string;
  changes: ChangelogEntry[];
}

export interface RepoActivity {
  score: number;
  weeks4: number;
  weeks13: number;
  weeks52: number;
}

export interface RepoEntry {
  org: string;
  name: string;
  fullName: string;
  description: string;
  url: string | null;
  readiness: string;
  /** True only if cloned to this container (repo_clones), not the baked-in mirror. */
  cloned?: boolean;
  /** True if mirrored read-only in the image under system/org/<org>/repos/. */
  mirrored?: boolean;
  markers?: string[];
  activity?: RepoActivity | null;
  updated?: string | null;
  docPath?: string | null;
  /** When readiness was last re-scanned live (ISO), or null if from the static table. */
  scannedAt?: string | null;
}

export interface DocEntry {
  path: string;
  dir: string;
  title: string;
  mtime?: string | null;
}

// ── Knowledge Graph ──────────────────────────────────────────────────────────
// One entry per thing that exists — a service, a repository, a Kafka topic, a
// datastore, a code area, a team, a person, a capability, a concept — holding its
// identity and its outbound relations, and pointing with `page` at the system doc
// that describes it. The graph is the relation layer over docs/, so the reader
// shows that doc whole with the relations either side of it.
// The contract lives in the corpus: system/org/<org>/knowledge-graph/SCHEMA.md.

/** How much to trust a relation. INFERRED is a hypothesis, not a fact. */
/** One post-delete link report, as GET /pages/deleted/reports returns it — the
 *  deletion it belongs to, plus its rows (ENG-55775 part 2). */
export interface DeletedLinkReportData {
  pagePath: string;
  title: string;
  section: string;
  slug: string;
  redirectTarget: string;
  deletedAt: string;
  deletedBy: string;
  entries: DevhubDeletedPageLinkEntry[];
  /** rows still needing a decision */
  open: number;
}

/** One row of the Recently deleted view (ENG-55775 part 3). */
export interface DeletedPageRow {
  pagePath: string;
  title: string;
  section: string;
  slug: string;
  /** null when the page was only ever published Hidden and was deleted with no
   *  redirect (ENG-60548) — a restore or purge then has none to remove */
  redirectTarget: string | null;
  deletedAt: string;
  deletedBy: string;
  expiresAt: string;
  restoredAt: string | null;
  restoredBy: string | null;
  prUrl: string | null;
  /** how many files the restore would write back */
  files: number;
  linkReport: DevhubDeletedPageLinkEntry[];
  restoreReport: DevhubDeletedPageLinkEntry[];
  openDeleteEntries: number;
  openRestoreEntries: number;
  restorable: boolean;
  /** why not, when it isn't — restored already, or the window has closed */
  blockedReason: string | null;
}

export type GraphTier = "FACT" | "CONFIRMED" | "INFERRED" | "SEED";

export interface GraphEntry {
  id: string;
  type: string;
  slug: string;
  title: string;
  summary: string;
  aliases: string[];
  status: string;
  tier: string;
  /** false once a human has written the page up. */
  seed: boolean;
  sources: string[];
  /** Short cited statements the entry carries itself — all a page-less entry has. */
  facts: string[];
  /**
   * The doc that DESCRIBES this thing, relative to the org root
   * ("docs/services/omni-api.md"), or null when nothing describes it yet.
   */
  page: string | null;
  /** Corpus-relative entry file: "entries/service/omni-api.yml". */
  entryPath: string;
  relationsOut: number;
  relationsIn: number;
  degree: number;
}

/** One row on a rail: a related entry, and how it relates from here. */
export interface RelatedEntry {
  id: string;
  type: string;
  title: string;
  summary: string;
  /** The relation read from the open page's point of view. */
  relation: string;
  evidence: string;
  tier: GraphTier | string;
  note: string;
}

/** One rail section — everything related to the open page by ONE relation. */
export interface RelationGroup {
  /** The relation as read from here — "produces", "contains", "owned by". */
  relation: string;
  /** The same, as a heading — "Produces", "Contains", "Owned by". */
  label: string;
  items: RelatedEntry[];
}

export interface GraphSummary {
  org: string;
  generatedAt: string;
  /** The entry to open first — the architecture page. */
  start: string | null;
  stats: {
    entries: number;
    relations: number;
    seeded: number;
    unreachable: number;
    byType: Record<string, number>;
    byTier: Record<string, number>;
  };
  entryTypes: string[];
  typeLabels: Record<string, string>;
  relationVocabulary: Record<string, { inverse: string; domain: string[] | "any"; range: string[] | "any" }>;
  hubs: GraphEntry[];
  trails: GraphEntry[];
}

export interface GraphEntryDetail {
  entry: GraphEntry;
  /** What relates TO this entry, grouped by relation — the left rail. */
  incoming: RelationGroup[];
  /** What this entry relates TO, grouped by relation — the right rail. */
  outgoing: RelationGroup[];
}

export interface MenuItem {
  title: string;
  path: string;
  /** Sort key mirroring the live menu position; when set, the nav interleaves
   *  items and sub-groups by this order instead of items-before-subgroups. */
  order?: number;
  /** True when this item is its group node's "self landing page" — a page whose
   *  slug/title names the group (or is index/overview). The nav promotes it to the
   *  group header (opens it, labels the header with its title) instead of listing
   *  it as a duplicate leaf beneath a same-named header. See pickGroupLanding(). */
  landing?: boolean;
}

export interface SkillResource {
  path: string;
  kind: string; // "reference" | "asset" | "script" | "eval"
  isMarkdown: boolean;
}

export interface SkillSummary {
  slug: string;
  name: string;
  title: string;
  description: string;
  triggers: string[];
  platforms: string[];
  version: string | null;
  resourceCount: number;
}

export interface SkillDetail extends SkillSummary {
  goal: string | null;
  outputFormat: string | null;
  body: string;
  resources: SkillResource[];
}

export interface MenuGroup {
  label: string;
  items: MenuItem[];
}
